/**
 * ConfirmationModal - Modale de confirmation réutilisable
 *
 * Props:
 * - isOpen: boolean - Afficher/masquer la modale
 * - onClose: () => void - Callback pour fermer la modale
 * - onConfirm: () => void - Callback pour confirmer l'action
 * - title: string - Titre de la modale
 * - message: string - Message de confirmation
 * - confirmText?: string - Texte du bouton de confirmation (défaut: "Confirm")
 * - cancelText?: string - Texte du bouton d'annulation (défaut: "Cancel")
 * - type?: 'danger' | 'warning' | 'info' - Type de modale (défaut: 'info')
 * - children?: React.ReactNode - Contenu additionnel (optionnel)
 */

import React from 'react';
import './ConfirmationModal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  children?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'info',
  children
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '⚡';
      case 'info':
        return 'ℹ️';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="confirmation-modal-backdrop" onClick={handleBackdropClick}>
      <div className={`confirmation-modal confirmation-modal-${type}`}>
        <div className="confirmation-modal-header">
          <span className="confirmation-modal-icon">{getIcon()}</span>
          <h3 className="confirmation-modal-title">{title}</h3>
          <button className="confirmation-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="confirmation-modal-body">
          <p className="confirmation-modal-message">{message}</p>
          {children}
        </div>

        <div className="confirmation-modal-footer">
          <button
            className="confirmation-modal-button confirmation-modal-button-cancel"
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            className={`confirmation-modal-button confirmation-modal-button-confirm confirmation-modal-button-${type}`}
            onClick={handleConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
